import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldCheck, ShieldX, Loader2, Key } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between py-3.5" style={{ borderBottom: '1px solid var(--lux-border-1)' }}>
    <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--lux-text-muted-2)' }}>{label}</span>
    <span className="text-sm font-medium text-right" style={{ color: 'var(--lux-text)' }} data-testid={testId}>{value}</span>
  </div>
);

export default function VerifyReceiptPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tenant = params.get('tenant');
    const id = params.get('id');
    if (!tenant || !id) { setLoading(false); setData({ valid: false }); return; }
    axios.get(`${API}/api/receipts/verify/${id}?tenant=${encodeURIComponent(tenant)}`, {
      headers: { 'X-Tenant-Slug': tenant },
    })
      .then(r => setData(r.data))
      .catch(() => setData({ valid: false }))
      .finally(() => setLoading(false));
  }, []);

  const valid = data?.valid;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-12"
      style={{ background: 'var(--lux-bg)', color: 'var(--lux-text)', fontFamily: "'DM Sans', sans-serif" }}
      data-testid="verify-receipt-page">

      <div className="w-full max-w-md fade-in">
        {loading ? (
          <div className="flex flex-col items-center gap-4 py-20">
            <Loader2 size={28} className="animate-spin" style={{ color: 'var(--lux-gold)' }} />
            <p className="text-xs uppercase tracking-[0.25em]" style={{ color: 'var(--lux-text-muted)' }}>Verifica in corso…</p>
          </div>
        ) : (
          <div className="rounded-sm overflow-hidden"
            style={{ background: 'var(--lux-surface-1)', border: `1px solid ${valid ? 'rgba(201,168,76,0.35)' : 'rgba(239,68,68,0.3)'}`, boxShadow: valid ? '0 24px 64px rgba(0,0,0,0.4), 0 0 40px rgba(201,168,76,0.08)' : '0 24px 64px rgba(0,0,0,0.4)' }}>

            {/* Header seal */}
            <div className="flex flex-col items-center text-center px-8 pt-10 pb-8"
              style={{ borderBottom: '1px solid var(--lux-border-1)', background: valid ? 'rgba(201,168,76,0.04)' : 'rgba(239,68,68,0.04)' }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                style={{
                  background: valid ? 'rgba(201,168,76,0.1)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${valid ? 'rgba(201,168,76,0.4)' : 'rgba(239,68,68,0.35)'}`,
                }}>
                {valid
                  ? <ShieldCheck size={28} style={{ color: 'var(--lux-gold)' }} strokeWidth={1.5} data-testid="verify-valid-badge" />
                  : <ShieldX size={28} style={{ color: '#ef4444' }} strokeWidth={1.5} data-testid="verify-invalid-badge" />}
              </div>
              <h1 className="font-serif text-2xl sm:text-3xl font-light mb-2">
                {valid ? 'Ricevuta Autentica' : 'Ricevuta non verificabile'}
              </h1>
              <p className="text-xs leading-relaxed max-w-xs" style={{ color: 'var(--lux-text-muted)' }}>
                {valid
                  ? 'Questo documento è stato emesso e registrato sulla piattaforma.'
                  : 'Il documento non risulta registrato oppure il link non è valido.'}
              </p>
            </div>

            {/* Details */}
            {valid && (
              <div className="px-8 py-6">
                <Row label="N. Ricevuta" value={data.receipt_number} testId="verify-receipt-number" />
                <Row label="Emessa da" value={data.company} testId="verify-company" />
                {data.vat_number && <Row label="P.IVA" value={data.vat_number} testId="verify-vat" />}
                <Row label="Ospite" value={data.guest_name} testId="verify-guest" />
                <Row label="Struttura" value={data.property_name} testId="verify-property" />
                <Row label="Check-in" value={data.checkin} testId="verify-checkin" />
                <Row label="Check-out" value={data.checkout} testId="verify-checkout" />
                <div className="flex items-center justify-between pt-5">
                  <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--lux-text-muted-2)' }}>Totale</span>
                  <span className="font-serif text-3xl font-light" style={{ color: 'var(--lux-gold)' }} data-testid="verify-total">
                    €{Number(data.total_price).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-center gap-2 mt-8">
          <div className="w-5 h-5 rounded-sm flex items-center justify-center" style={{ background: 'var(--lux-gold)' }}>
            <Key size={10} color="#000" strokeWidth={2.5} />
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--lux-text-muted-3)' }}>
            Verificato da Shangrilux Luxury Hospitality
          </p>
        </div>
      </div>
    </div>
  );
}
